import { useEffect, useRef } from 'react'
import { useAuditFeed } from '../../../../hooks/useAuditFeed'
import { useChainIntegrity } from '../../../../hooks/useChainIntegrity'
import { getRuntime } from '../../../../hooks/_mode'
import { useGovernance } from './GovernanceProvider'
import { deriveDurableStatus, auditEventToRecord } from './governanceDurable'

/**
 * GovernanceDurableBridge — connects the real canonical trust sources into the
 * governance ledger. Reads olv_audit_trail (useAuditFeed) and the
 * olv_verify_event_chain RPC (useChainIntegrity). When the runtime is live AND
 * authenticated, it hydrates the Audit Trail with durable rows (labeled
 * saved_durable) and records the integrity-chain verdict. In demo mode or
 * without a session it settles the honest local-session status and leaves the
 * labeled ledger untouched. Renders nothing.
 */
export default function GovernanceDurableBridge() {
  const gov = useGovernance()
  const runRef = useRef(gov.run)
  runRef.current = gov.run

  const audit = useAuditFeed()
  const chain = useChainIntegrity(audit.events.length)
  const settledLocalRef = useRef(false)

  useEffect(() => {
    if (audit.loading || chain.loading) return
    const { live } = getRuntime()

    if (!live) {
      if (settledLocalRef.current) return
      settledLocalRef.current = true
      runRef.current({
        type: 'set_durable_status',
        status: deriveDurableStatus({
          live: false, auditAuthRequired: false, chainAuthRequired: false, auditError: false,
          auditCount: 0, chainValid: false, chainMessage: chain.message, missingRpc: chain.missingRpc,
        }),
      })
      return
    }

    const status = deriveDurableStatus({
      live: true,
      auditAuthRequired: audit.authRequired,
      chainAuthRequired: chain.authRequired,
      auditError: audit.loadError,
      auditCount: audit.events.length,
      chainValid: chain.valid,
      chainMessage: chain.message,
      missingRpc: chain.missingRpc,
    })
    runRef.current({ type: 'set_durable_status', status })

    if (status.mode === 'durable' && audit.events.length > 0) {
      runRef.current({
        type: 'hydrate_durable',
        module: 'audit',
        records: audit.events.slice(0, 100).map(auditEventToRecord),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit.events, audit.loading, audit.authRequired, audit.loadError, chain.loading, chain.authRequired, chain.valid, chain.message, chain.missingRpc])

  return null
}
